import React, { useState, useEffect } from 'react';
import useSWR, { mutate } from 'swr';
import { useLocation } from 'react-router-dom';
import {
  Form,
  Col,
  Row,
  Button,
  Card,
  Alert,
  Modal,
  Spinner,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { fisio as dbfisio } from '../../../helpers/laudos/fisio';
import { dobuta as dbdobuta } from '../../../helpers/laudos/dobuta';
import CustomFileInput from '../../../components/CustomFileInput';
import useViewFiles from '../../../hooks/useViewFiles';
import { liberarLaudoByPac } from '../../../services/prontuario';
import { fetchStressByCod, updateStressById } from '../../../services/stress';
import useUploadFiles from '../../../hooks/useUploadFiles';
import generateStressPDF from './generateStressPDF';
import FileCarousel from '../../../components/laudos/FileCarousel';
import FontScaleButtonGroup from '../../../components/FontScaleButtonGroup';
import Footer from '../../../components/laudos/Footer';
import LaudoCardHeader from '../../../components/laudos/LaudoCardHeader';
import { getPrefix } from '../../../util/utils';
import ModalStress from './ModalStress';
import useIsMedicoExterno from '../../../hooks/useIsMedicoExterno';

const FISIO_TIPOS = [
  {
    id: 'efPositivo',
    label: 'Estresse Fisiológico POSITIVO',
    TIPOSTRESS: 'Estresse Fisiológico POSITIVO',
    ISQUEMIA: dbfisio[0].normalPositivo,
  },
  {
    id: 'efNegativo',
    label: 'Estresse Fisiológico NEGATIVO',
    TIPOSTRESS: 'Estresse Fisiológico NEGATIVO',
    ISQUEMIA: dbfisio[1].normalNegativo,
  },
  {
    id: 'efaPositivo',
    label: 'Estresse Fisiológico Alterado POSITIVO',
    TIPOSTRESS: 'Estresse Fisiológico Alterado POSITIVO',
    ISQUEMIA: dbfisio[2].alteradoPositivo,
  },
  {
    id: 'efaNegativo',
    label: 'Estresse Fisiológico Alterado NEGATIVO',
    TIPOSTRESS: 'Estresse Fisiológico Alterado NEGATIVO',
    ISQUEMIA: dbfisio[3].alteradoNegativo,
  },
];

const DOBUTA_TIPOS = [
  {
    id: 'ivmPositivo',
    label: 'Isquemia e Viabilidade Miocárdica POSITIVO',
    TIPOSTRESS: 'Isquemia e Viabilidade Miocárdica POSITIVO',
    ISQUEMIA: dbdobuta[0].ivPositivo,
  },
  {
    id: 'ivmNegativo',
    label: 'Isquemia e Viabilidade Miocárdica NEGATIVO',
    TIPOSTRESS: 'Isquemia e Viabilidade Miocárdica NEGATIVO',
    ISQUEMIA: dbdobuta[1].ivNegativo,
  },
  {
    id: 'imPositivo',
    label: 'Isquemia Miocárdica POSITIVO',
    TIPOSTRESS: 'Isquemia Miocárdica POSITIVO',
    ISQUEMIA: dbdobuta[2].imPositivo,
  },
  {
    id: 'imNegativo',
    label: 'Isquemia Miocárdica NEGATIVO',
    TIPOSTRESS: 'Isquemia Miocárdica NEGATIVO',
    ISQUEMIA: dbdobuta[3].imNegativo,
  },
  {
    id: 'imaPositivo',
    label: 'Isquemia Miocárdica Alterado POSITIVO',
    TIPOSTRESS: 'Isquemia Miocárdica Alterado POSITIVO',
    ISQUEMIA: dbdobuta[4].imaPositivo,
  },
  {
    id: 'imaNegativo',
    label: 'Isquemia Miocárdica Alterado NEGATIVO',
    TIPOSTRESS: 'Isquemia Miocárdica Alterado NEGATIVO',
    ISQUEMIA: dbdobuta[5].imaNegativo,
  },
];

const TIPO_STRESS_MAP = [...FISIO_TIPOS, ...DOBUTA_TIPOS].reduce(
  (acc, tipo) => {
    acc[tipo.id] = {
      TIPOSTRESS: tipo.TIPOSTRESS,
      ISQUEMIA: tipo.ISQUEMIA,
    };
    return acc;
  },
  {}
);

export default function FormStress() {
  const location = useLocation();
  const codLaudo = location.state && location.state.codLaudo;
  const medico = location.state && location.state.medico;
  const isMedicoExterno = useIsMedicoExterno();
  const { data, error, isLoading } = useSWR(
    codLaudo ? `stress-${codLaudo}` : null,
    () => fetchStressByCod(codLaudo)
  );
  const [stress, setStress] = useState(null);
  const [showModalStress, setShowModalStress] = useState(false);
  const {
    urls,
    setURLs,
    loadingFiles,
    showModal: showStressFilesModal,
    setShowModal: setShowStressFilesModal,
    showFiles,
  } = useViewFiles({
    codLaudo: stress?.CODSTRESS,
    procedimento: 'stress',
  });
  const [restaurarStress, setRestaurarStress] = useState(false);
  const [fontScale, setFontScale] = useState(0.95);

  useEffect(() => {
    if (data) setStress(data);
  }, [data]);

  const {
    files,
    setFiles,
    showUpload,
    setShowUpload,
    isUploading,
    confirmUpload,
    handleCloseModal,
  } = useUploadFiles({ atividade: stress });

  const handleTipoStress = tipo => {
    const patch = TIPO_STRESS_MAP[tipo];
    if (!patch) return;
    setStress({ ...stress, ...patch });
    document.getElementById('editDescricao').focus();
  };

  async function handleGravar(e, tipo) {
    try {
      e.preventDefault();
      const updatedStress = { ...stress, tipoGravacao: tipo };
      const res = await updateStressById(updatedStress);
      if (tipo === 'definitivo') {
        const fatura = {
          codLaudo: updatedStress.CODSTRESS,
          codPac: updatedStress.CODPAC,
        };
        await liberarLaudoByPac({ fatura });
      }
      toast.success(res.data.message);
      mutate(`stress-${codLaudo}`);
    } catch (error) {
      setRestaurarStress(false);
      toast.error(error?.response?.data?.error || 'Erro ao gravar stress!');
    }
  }

  if (error) {
    return (
      <Alert variant="danger" style={{ marginTop: 10 }}>
        <Alert.Heading>Erro ao carregar laudo de stress!</Alert.Heading>
        <p>Tente novamente.</p>
      </Alert>
    );
  }

  if (isLoading || !stress) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <Spinner animation="border" variant="dark" size="lg" />
      </div>
    );
  }

  return (
    <>
      <Card style={{ marginTop: 10 }}>
        <LaudoCardHeader title="ECOCARDIOGRAMA ESTRESSE" />
        {!isMedicoExterno && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              padding: '8px 16px 0 16px',
            }}
          >
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowModalStress(true)}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <span style={{ marginRight: 6 }}>
                <svg
                  width="1em"
                  height="1em"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M10 1.5A1.5 1.5 0 0 1 11.5 3v1h-7V3A1.5 1.5 0 0 1 6 1.5h4zm1.5 2A.5.5 0 0 0 11 3V1.5A2.5 2.5 0 0 0 8.5-1h-4A2.5 2.5 0 0 0 2 1.5V3a.5.5 0 0 0-.5.5v10A2.5 2.5 0 0 0 4 16h8a2.5 2.5 0 0 0 2.5-2.5v-10A.5.5 0 0 0 14 3V1.5A1.5 1.5 0 0 0 12.5 0h-4A1.5 1.5 0 0 0 7 1.5V3a.5.5 0 0 0-.5.5v10A1.5 1.5 0 0 0 8 15h4a1.5 1.5 0 0 0 1.5-1.5v-10z" />
                </svg>
              </span>
              Copiar Laudo Anterior
            </Button>
          </div>
        )}
        <Card.Body>
          <Form noValidate>
            <strong className="mr-auto">Nome do Paciente: </strong>
            {`${stress?.CODPAC} - ${stress?.NOMEPAC}`}
            <br></br>
            <strong className="mr-auto">Data de Nasc: </strong>
            {`${stress?.dataNasc}`}
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Sexo: </strong>
            {`${stress?.SEXOPAC}`}
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Idade: </strong>
            {`${stress?.idadePAC} anos`}
            <br></br>
            <strong className="mr-auto">Convênio: </strong>
            {stress?.SIGLACONV}
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Cod Stress: </strong>
            {stress?.CODSTRESS}
            &nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Data do Exame: </strong>
            {stress?.dataStress}
            <br></br>
            <strong className="mr-auto">
              Médico que Realizou: {getPrefix(stress?.CODREALIZOU)}{' '}
            </strong>
            {`${stress?.Realizou}`}
            <br></br>
            <strong className="mr-auto">
              Médico que Solicitou: {getPrefix(stress?.SOLICITOU)}{' '}
            </strong>
            {`${stress?.Solicitou}`}
            {!isMedicoExterno && (
              <>
                <Card className="mt-2" style={{ fontSize: 14 }}>
                  <Row xs={12} sm={6} md={2} lg={2}>
                    <Col sm={6} style={{ align: 'left' }}>
                      <strong>ESTRESSE FISIO</strong>
                      {FISIO_TIPOS.map(({ id, label }) => (
                        <Form.Check
                          key={id}
                          label={label}
                          name="groupStress"
                          type="radio"
                          id={id}
                          disabled={stress?.HASH}
                          onClick={event => handleTipoStress(event.target.id)}
                        />
                      ))}
                    </Col>
                    <Col sm={6} style={{ align: 'left' }}>
                      <strong>ESTRESSE DOBUTA</strong>
                      {DOBUTA_TIPOS.map(({ id, label }) => (
                        <Form.Check
                          key={id}
                          label={label}
                          name="groupStress"
                          type="radio"
                          id={id}
                          disabled={stress?.HASH}
                          onClick={event => handleTipoStress(event.target.id)}
                        />
                      ))}
                    </Col>
                  </Row>
                </Card>
                {medico && (
                  <>
                    <Card className="mt-2" style={{ fontSize: 14 }}>
                      <Row xs={12} sm={6} md={2} lg={2}>
                        <Col sm={6} style={{ align: 'left' }}>
                          <Form.Check
                            className="m-2"
                            type="checkbox"
                            checked={!!restaurarStress}
                            label="Restaurar Stress"
                            onChange={e => {
                              setRestaurarStress(e.target.checked);
                            }}
                          />
                        </Col>
                      </Row>
                    </Card>
                    <Alert
                      show={restaurarStress}
                      variant="warning"
                      style={{ textAlign: 'right' }}
                    >
                      <Alert.Heading>
                        {medico.codMed === stress?.CODREALIZOU ? (
                          <>
                            <span>
                              Dr. {medico.apelido}, confirma a restauração deste
                              Laudo de Stress?
                            </span>
                            <Button
                              className="text-right"
                              id="btnUpdateFat"
                              disabled={restaurarStress ? false : true}
                              onClick={e => handleGravar(e, 'parcial')}
                              variant="warning"
                            >
                              Restaurar Stress
                            </Button>
                          </>
                        ) : (
                          <span>
                            Dr. {medico.apelido}, somente o Médico que realizou o
                            exame pode restaurar este Laudo de Stress!
                          </span>
                        )}
                      </Alert.Heading>
                    </Alert>
                  </>
                )}
              </>
            )}
            &nbsp;
            <Form.Row>
              <Col sm>
                <Form.Group size="sm" controlId="editDescricao">
                  <Form.Label className="font-weight-bold">
                    DESCRIÇÃO
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={stress?.ISQUEMIA?.split('\n').length + 3 || 2}
                    name="ISQUEMIA"
                    readOnly={!!stress?.HASH || isMedicoExterno}
                    value={stress?.ISQUEMIA ?? ''}
                    onChange={e =>
                      setStress({ ...stress, ISQUEMIA: e.target.value })
                    }
                    onKeyDown={e => {
                      if (e.key === 'F2') {
                        const posStart = stress?.ISQUEMIA.indexOf('---');
                        const memoIsquemia = document.getElementById(
                          'editDescricao'
                        );
                        memoIsquemia.setSelectionRange(posStart, posStart + 3);
                      }
                    }}
                  />
                </Form.Group>
              </Col>
            </Form.Row>
            {!isMedicoExterno && (
              <FontScaleButtonGroup
                fontScale={fontScale}
                setFontScale={setFontScale}
              />
            )}
            <Footer
              gravarParcial={e => handleGravar(e, 'parcial')}
              gravarDefinitivo={e => handleGravar(e, 'definitivo')}
              imprimirPdfNormal={() =>
                generateStressPDF(stress, 'imprimirNormal', fontScale)
              }
              imprimirPdfTimbrado={() =>
                generateStressPDF(stress, 'imprimirTimbrado', fontScale)
              }
              enviarPorWpp={() =>
                generateStressPDF(stress, 'envioWpp', fontScale)
              }
              verImagens={showFiles}
              abrirUploadModal={() => setShowUpload(true)}
              laudo={stress}
            />
          </Form>
        </Card.Body>
      </Card>

      <Modal
        show={showStressFilesModal}
        onHide={() => setShowStressFilesModal(false)}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Imagens</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingFiles ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh',
              }}
            >
              <Spinner animation="border" variant="dark" size="lg" />
            </div>
          ) : (
            <FileCarousel urls={urls} setURLs={setURLs} />
          )}
        </Modal.Body>
      </Modal>

      <Modal show={showUpload} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Anexar arquivo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CustomFileInput
            id="file-upload"
            label="Selecionar arquivo"
            multiple={true}
            onChange={e => setFiles(Array.from(e.target.files))}
          />
          <ul>{files && files.map(file => <li>{file.name}</li>)}</ul>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={confirmUpload} disabled={isUploading}>
            {isUploading ? 'Enviando...' : 'Confirmar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <ModalStress
        show={showModalStress}
        onHide={() => setShowModalStress(false)}
        codPac={stress?.CODPAC} // Ou stress?.codPac, dependendo de como a API retorna
        onCopy={data => {
          if (!data) return;

          setStress(prevStress => ({
            ...prevStress,
            ISQUEMIA: data?.ISQUEMIA,
            TIPOSTRESS: data?.TIPOSTRESS,
          }));

          toast.success('Laudo de Stress copiado com sucesso!');
        }}
      />
    </>
  );
}
